import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { ContactoForm } from "@/pages/Contactos";

const v = (c, k) => (c && c[k] && c[k].valor != null && (c[k].confianza ?? 1) > 0 ? String(c[k].valor).trim() : "");
export const esContacto = (analisis) => {
  const c = analisis?.contacto;
  return !!c && (c.es_contacto?.valor === true || c.es_contacto?.valor === "true") && !!(v(c, "telefono") || v(c, "email") || v(c, "nombre_persona") || v(c, "empresa"));
};

// Solo lo que la IA ha leído en la imagen; lo demás queda vacío para que el usuario lo complete.
const datosDe = (c) => {
  const persona = v(c, "nombre_persona"), empresa = v(c, "empresa");
  const notas = [["Otro teléfono", v(c, "telefono_2")], ["Web", v(c, "web")], ["Otros datos", v(c, "otros")]]
    .filter(([, x]) => x).map(([k, x]) => `${k}: ${x}`).join("\n");
  return { nombre: persona && empresa ? `${persona} (${empresa})` : persona || empresa, telefono: v(c, "telefono"),
           email: v(c, "email"), direccion: v(c, "direccion"), categoria: "otros", notas };
};

const PreguntaImagen = ({ busy, onConservar, onDescartar }) => (
  <div className="mt-4 bg-[#E8F0EC] rounded-2xl p-4 space-y-3" data-testid="contacto-imagen-pregunta">
    <p className="font-semibold text-[#1C2421] text-lg">Contacto guardado. ¿Qué hago con la imagen?</p>
    <button data-testid="contacto-imagen-conservar" disabled={busy} onClick={onConservar}
      className="w-full min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base">Guardar también la imagen en Documentos</button>
    <button data-testid="contacto-imagen-descartar" disabled={busy} onClick={onDescartar}
      className="w-full min-h-[52px] rounded-xl bg-white border border-[#D6CFC2] text-[#1C2421] font-semibold text-base">No conservar la imagen</button>
  </div>
);

// Si el documento ya no existe (borrado en otra pantalla), el contacto queda guardado y se cierra sin bloquear.
const useDocDesaparecido = (onTerminar) => {
  const { recargarHogar } = useData();
  return (e) => {
    if (e.response?.status !== 404) return false;
    toast.info("Contacto guardado. Este documento ya no estaba en AL DÍA.");
    recargarHogar().catch(() => {});
    onTerminar();
    return true;
  };
};

const useDescartar = (docId, onTerminar) => {
  const { recargarHogar } = useData();
  const [busy, setBusy] = useState(false);
  const desaparecido = useDocDesaparecido(onTerminar);
  const descartar = async () => {
    setBusy(true);
    try {
      await api.post(`/documentos/${docId}/descartar`); toast.success("Contacto guardado. La imagen no se conserva.");
      recargarHogar().catch(() => {}); onTerminar();
    }
    catch (e) { if (!desaparecido(e)) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo quitar la imagen."); setBusy(false); } }
  };
  return { busy, setBusy, descartar, desaparecido };
};

// Tarjeta en la revisión del documento: «Añadir a Contactos» y, tras guardar, qué hacer con la imagen.
export const ContactoDetectado = ({ analisis, docId, onTerminar, acciones = null }) => {
  const { crearContacto, actualizarContacto } = useData();
  const [editing, setEditing] = useState(null);
  const [preguntaImagen, setPreguntaImagen] = useState(false);
  const { busy, descartar } = useDescartar(docId, onTerminar);
  if (preguntaImagen) return <PreguntaImagen busy={busy} onDescartar={descartar}
    onConservar={() => { toast.success("Imagen guardada en Documentos como referencia"); onTerminar(); }} />;
  return (
    <div className="mt-4 bg-[#E8F0EC] border border-[#CFE0D6] rounded-2xl p-4" data-testid="contacto-detectado">
      <p className="font-semibold text-[#1C2421] flex items-center gap-2"><Icons.Contact size={20} className="text-[#3A6B56]" /> Parece un contacto</p>
      <p className="text-base text-[#4A5550] mt-1">Puedes guardarlo en Contactos sin crear ningún asunto. Revisa los datos antes de guardar.</p>
      <button data-testid="contacto-detectado-anadir" onClick={() => setEditing(datosDe(analisis.contacto))}
        className="mt-3 w-full min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base inline-flex items-center justify-center gap-2">
        <Icons.UserPlus size={18} /> Añadir a Contactos
      </button>
      {acciones}
      <ContactoForm editing={editing} onClose={() => setEditing(null)} crearContacto={crearContacto} actualizarContacto={actualizarContacto}
        comprobarDuplicados onSaved={() => setPreguntaImagen(true)} />
    </div>
  );
};

// Asignar › Contacto en un documento ya guardado: datos ya leídos por la IA (o vacío), sin volver a llamar a la IA.
export const ContactoDesdeDocumento = ({ doc, onCancelar, onTerminar }) => {
  const { crearContacto, actualizarContacto, vincularDocumentoContacto } = useData();
  const [editing, setEditing] = useState(() => (!doc.sin_ia && esContacto(doc.analisis) ? datosDe(doc.analisis.contacto) : { categoria: "otros" }));
  const [contactoId, setContactoId] = useState(null);
  const { busy, setBusy, descartar, desaparecido } = useDescartar(doc.id, onTerminar);
  const conservar = async () => {
    setBusy(true);
    try { await vincularDocumentoContacto(doc.id, contactoId); toast.success("Imagen guardada en Documentos › Contactos"); onTerminar(); }
    catch (e) { if (!desaparecido(e)) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo guardar la imagen."); setBusy(false); } }
  };
  useEffect(() => { if (!editing && !contactoId) onCancelar(); }, [editing, contactoId, onCancelar]);
  if (contactoId) return <PreguntaImagen busy={busy} onConservar={conservar} onDescartar={descartar} />;
  return <ContactoForm editing={editing} onClose={() => setEditing(null)} crearContacto={crearContacto}
    actualizarContacto={actualizarContacto} comprobarDuplicados onSaved={(id) => setContactoId(id)} />;
};

// En la revisión de un contacto: dejarlo en Pendientes o ver las demás opciones de clasificación.
export const OpcionesContacto = ({ onPendiente, onOtroSitio }) => (
  <div className="mt-2 space-y-2">
    <button data-testid="contacto-dejar-pendiente" onClick={onPendiente}
      className="w-full min-h-[52px] rounded-xl bg-white border border-[#D6CFC2] text-[#1C2421] font-semibold text-base">Dejar en Pendientes de clasificar</button>
    <button data-testid="contacto-otro-sitio" onClick={onOtroSitio}
      className="w-full min-h-[44px] text-[#4A5550] font-semibold text-base underline underline-offset-4">Guardarlo en otro sitio</button>
  </div>
);

// La imagen ya estaba guardada: crear el contacto desde el documento existente, sin guardar la imagen dos veces.
export const ContactoYaGuardado = ({ duplicados, analisis, docId, onGuardarIgual, onCancelar, onTerminar }) => {
  const { documents } = useData();
  const [existente, setExistente] = useState(null);
  const [busy, setBusy] = useState(false);
  const previo = duplicados.map((c) => (documents || []).find((d) => d.id === c.id)).find(Boolean);
  const usarExistente = async () => {
    setBusy(true);
    try { await api.post(`/documentos/${docId}/descartar`); setExistente({ ...previo, sin_ia: false, analisis }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo continuar."); setBusy(false); }
  };
  if (existente) return <ContactoDesdeDocumento doc={existente} onCancelar={onTerminar} onTerminar={onTerminar} />;
  return (
    <div className="mt-4 bg-[#FFF7ED] border border-[#FED7AA] rounded-2xl p-4 space-y-3" data-testid="contacto-ya-guardado">
      <p className="font-semibold text-[#1C2421] text-base">Ya tienes esta imagen guardada{previo ? <>: «{previo.filename}»</> : ""}.</p>
      {previo && <button data-testid="contacto-ya-guardado-usar" disabled={busy} onClick={usarExistente}
        className="w-full min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base inline-flex items-center justify-center gap-2">
        <Icons.UserPlus size={18} /> Añadir a Contactos desde el documento que ya tienes
      </button>}
      <button data-testid="contacto-ya-guardado-igual" disabled={busy} onClick={onGuardarIgual}
        className="w-full min-h-[48px] rounded-xl bg-white border border-[#D6CFC2] text-[#1C2421] font-semibold text-base">Guardar de todos modos</button>
      <button data-testid="contacto-ya-guardado-cancelar" disabled={busy} onClick={onCancelar}
        className="w-full min-h-[44px] rounded-xl bg-white text-[#B91C1C] font-semibold text-sm border border-[#E5DFD5]">Cancelar y descartar</button>
    </div>
  );
};
